import { trpc } from "@/lib/trpc";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SeoTags from "@/components/SeoTags";
import { useIsMobile } from "@/hooks/useMobile";
import { useProductAnalytics } from "@/hooks/useProductAnalytics";
import MarketProductCard from "@/components/market/MarketProductCard";
import TwoToneHeading from "@/components/market/TwoToneHeading";
import { toDisplayAssetUrl } from "@/lib/assets";
import { getCatalogFilter, getCatalogFilterPath } from "@/lib/routes";
import { parseResolvedMarketChange } from "@/lib/marketChange";
import {
  MARKET_SHORTCUTS,
  MOVERS_MIN_PRICE,
  PHOTO_PLATE_CLASS,
  formatChange,
  getChangeColorClass,
  getMarketChangeValue,
  getSalesCount,
  getSortedPrices,
  getTrailingMedians,
  type PricePoint,
} from "@/lib/marketDisplay";
import {
  PAYPAL_FEE_RATE,
  PAYPAL_FIXED_FEE,
  PLATFORM_FEE_RATE,
  getSellerPayoutBreakdown,
} from "@/lib/sellerFees";
import { getProductPath } from "@shared/productRouting";
import { HOME_HERO_DESKTOP_MIN_WIDTH_PX, HOME_HERO_FIRST_SLIDE } from "@shared/homeHero";
import { getModelFamilyGuidePath } from "@shared/modelFamilyGuides";
import { buildHomeSeo } from "@shared/seo";
import type { Product } from "@shared/types";
import useEmblaCarousel from "embla-carousel-react";
import type { CSSProperties } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import {
  TrendingUp,
  TrendingDown,
} from "lucide-react";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const exactCurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const salesCountFormatter = new Intl.NumberFormat("en-US");

const chartDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

const releaseDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

function formatPrice(value: number | null | undefined) {
  return value == null ? "—" : currencyFormatter.format(value);
}

function formatExactPrice(value: number | null | undefined) {
  return value == null ? "—" : exactCurrencyFormatter.format(value);
}

function formatReleaseDate(value: string | null | undefined) {
  if (!value) return null;

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : releaseDateFormatter.format(parsed);
}

type HeroSlide = {
  ariaLabel: string;
  href: string;
  imageUrl: string;
  mobileImageUrl: string;
};

const HERO_SLIDES: HeroSlide[] = [
  HOME_HERO_FIRST_SLIDE,
  {
    ariaLabel: "Go to the sell page",
    href: "/sell",
    imageUrl:
      "https://imagedelivery.net/-LSpQIg39J0gXmiDBooAYw/a3eb6dc9-45f3-4691-076e-54340d632100/public",
    mobileImageUrl:
      "https://imagedelivery.net/-LSpQIg39J0gXmiDBooAYw/2f27012c-3111-4256-0277-2d4bc00b2700/public",
  },
  {
    ariaLabel: "Go to the Xbox Series X product page",
    href: getProductPath({
      name: "Microsoft Xbox Series X",
      deviceType: "gameconsoles",
    }),
    imageUrl:
      "https://imagedelivery.net/-LSpQIg39J0gXmiDBooAYw/c29ea5d8-7cc8-458e-0231-026e136d7d00/public",
    mobileImageUrl:
      "https://imagedelivery.net/-LSpQIg39J0gXmiDBooAYw/7faa3750-5f16-4c3e-db45-41b1fdcc0600/public",
  },
];

const SONIC_RUNNING_GIF_URL =
  "https://media.tenor.com/OTDlqAguqpEAAAAi/sonic-running.gif";

type MoversTab = "gainers" | "losers" | "watched";

const MOVERS_TABS: { value: MoversTab; label: string }[] = [
  { value: "gainers", label: "Gainers" },
  { value: "losers", label: "Losers" },
  { value: "watched", label: "Most watched" },
];

const MOVERS_LIMIT = 5;

// In focus: the most-watched device over the movers' price floor with enough recent sales to
// chart. The first few trending devices are checked in order, so a launch with only a handful of
// sales is skipped.
const IN_FOCUS_CANDIDATE_COUNT = 3;
const IN_FOCUS_MIN_SALES = 30;

type ChartRange = "1W" | "1M" | "3M" | "1Y";

const CHART_RANGES: { value: ChartRange; title: string }[] = [
  { value: "1W", title: "Sold prices, last week" },
  { value: "1M", title: "Sold prices, last month" },
  { value: "3M", title: "Sold prices, last 3 months" },
  { value: "1Y", title: "Sold prices, last year" },
];

const PRICING_POINTS = [
  {
    title: "Sold prices only",
    description: "We price from listings that actually sold on eBay and on techfare.",
  },
  {
    title: "The 30-day median",
    description:
      "Sales under half or over double the median are set aside, so outliers can't skew the price.",
  },
  {
    title: "Trends that hold up",
    description: "Busy models compare week over week, quieter ones across 30 days.",
  },
] as const;

const POPULAR_VALUE_GUIDES = [
  {
    slug: "apple",
    title: "iPhone market value",
    description: "Live iPhone prices and the generations holding value best.",
  },
  {
    slug: "ipad",
    title: "How much is my iPad worth?",
    description: "mini, Air and Pro values by storage and generation.",
  },
  {
    slug: "samsung",
    title: "Samsung market value",
    description: "Galaxy flagships, foldables and the releases in demand.",
  },
  {
    slug: "playstation",
    title: "PlayStation worth",
    description: "PS5, PS4 and older Sony consoles side by side.",
  },
  {
    slug: "xbox",
    title: "Xbox worth",
    description: "Series X, Series S and older Xbox resale values.",
  },
  {
    slug: "nintendo",
    title: "Nintendo value guides",
    description: "Switch, NES, SNES and N64 prices with collector context.",
  },
] as const;

const POPULAR_MODEL_FAMILY_GUIDES = [
  { slug: "iphone-6", title: "iPhone 6" },
  { slug: "iphone-se", title: "iPhone SE" },
  { slug: "nintendo-switch", title: "Nintendo Switch" },
  { slug: "ps5", title: "PS5" },
  { slug: "original-xbox", title: "Original Xbox" },
  { slug: "ipad-pro", title: "iPad Pro" },
] as const;

const MOBILE_BREAKPOINT_PX = HOME_HERO_DESKTOP_MIN_WIDTH_PX;
const HERO_DRAG_THRESHOLD_PX = 6;

function interleaveTickerProducts(products: Product[]) {
  if (products.length === 0) return [];

  const productsByCategory = new Map<number, Product[]>();
  for (const product of products) {
    const categoryProducts = productsByCategory.get(product.categoryId);
    if (categoryProducts) {
      categoryProducts.push(product);
    } else {
      productsByCategory.set(product.categoryId, [product]);
    }
  }

  const orderedCategoryIds = Array.from(productsByCategory.keys()).sort(
    (a, b) => a - b
  );
  const interleaved: Product[] = [];
  let hasRemainingProducts = true;

  while (hasRemainingProducts) {
    hasRemainingProducts = false;

    for (const categoryId of orderedCategoryIds) {
      const nextProduct = productsByCategory.get(categoryId)?.shift();
      if (!nextProduct) continue;

      interleaved.push(nextProduct);
      hasRemainingProducts = true;
    }
  }

  return interleaved;
}

function getMovers(
  allProducts: Product[],
  trendingProducts: Product[]
): Record<MoversTab, Product[]> {
  const ranked = allProducts
    .filter(
      product =>
        product.currentPrice >= MOVERS_MIN_PRICE &&
        getMarketChangeValue(product) != null
    )
    .sort(
      (a, b) => (getMarketChangeValue(b) ?? 0) - (getMarketChangeValue(a) ?? 0)
    );

  return {
    gainers: ranked
      .filter(product => (getMarketChangeValue(product) ?? 0) > 0)
      .slice(0, MOVERS_LIMIT),
    losers: ranked
      .filter(product => (getMarketChangeValue(product) ?? 0) < 0)
      .reverse()
      .slice(0, MOVERS_LIMIT),
    watched: trendingProducts
      .filter(product => product.currentPrice >= MOVERS_MIN_PRICE)
      .slice(0, MOVERS_LIMIT),
  };
}

const CHART_WIDTH = 600;
const CHART_HEIGHT = 160;

function getNiceStep(roughStep: number) {
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const fraction = roughStep / magnitude;
  const niceFraction = fraction < 1.5 ? 1 : fraction < 3 ? 2 : fraction < 7 ? 5 : 10;
  return niceFraction * magnitude;
}

function buildPriceChart(points: PricePoint[]) {
  const sorted = getSortedPrices(points);
  if (sorted.length < 2) return null;

  const windowDays = sorted.length >= 21 ? 7 : 3;
  const trend = getTrailingMedians(
    sorted.map(point => point.price),
    windowDays
  );
  const trendLow = Math.min(...trend);
  const trendHigh = Math.max(...trend);
  const padding = (trendHigh - trendLow) * 0.2 || trendHigh * 0.05;
  const low = Math.max(0, trendLow - padding);
  const high = trendHigh + padding;
  const toX = (index: number) => (index / (trend.length - 1)) * CHART_WIDTH;
  const toY = (value: number) => CHART_HEIGHT - ((value - low) / (high - low)) * CHART_HEIGHT;
  const trendPath = trend
    .map((value, index) => `${index === 0 ? "M" : "L"}${toX(index).toFixed(1)},${toY(value).toFixed(1)}`)
    .join(" ");
  const step = getNiceStep((high - low) / 3);
  const ticks: { label: string; top: number }[] = [];
  for (let tick = Math.ceil(low / step) * step; tick <= high; tick += step) {
    ticks.push({
      label: formatPrice(tick),
      top: (toY(tick) / CHART_HEIGHT) * 100,
    });
  }
  const lastIndex = sorted.length - 1;
  const labelIndexes = Array.from(
    new Set([0, Math.round(lastIndex / 3), Math.round((2 * lastIndex) / 3), lastIndex])
  );

  return {
    windowDays,
    trendPath,
    areaPath: `${trendPath} L${CHART_WIDTH},${CHART_HEIGHT} L0,${CHART_HEIGHT} Z`,
    ticks,
    dateLabels: labelIndexes.map(index => ({
      label: chartDateFormatter.format(sorted[index].recordedAt),
      left: (index / lastIndex) * 100,
      align: index === 0 ? "start" : index === lastIndex ? "end" : "center",
    })),
    firstTrend: trend[0],
    lastTrend: trend[trend.length - 1],
  };
}

export default function Home() {
  const seoMetadata =
    typeof window === "undefined" ? buildHomeSeo() : buildHomeSeo(window.location.origin);
  const isMobile = useIsMobile();
  const isInitialMobileViewport =
    typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT_PX;
  const shouldDeferTickerCatalog = isMobile || isInitialMobileViewport;
  const [isTickerCatalogReady, setIsTickerCatalogReady] = useState(
    !shouldDeferTickerCatalog
  );
  const [heroViewportRef, heroApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    loop: HERO_SLIDES.length > 1,
  });
  const heroPressRef = useRef({
    active: false,
    didDrag: false,
    startX: 0,
  });
  const [moversTab, setMoversTab] = useState<MoversTab>("gainers");
  const [chartRange, setChartRange] = useState<ChartRange>("3M");

  useEffect(() => {
    if (!shouldDeferTickerCatalog) {
      setIsTickerCatalogReady(true);
      return;
    }

    setIsTickerCatalogReady(false);

    let isCancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let idleId: number | null = null;
    const enableTickerCatalog = () => {
      if (!isCancelled) {
        setIsTickerCatalogReady(true);
      }
    };

    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(enableTickerCatalog, {
        timeout: 1500,
      });
    } else {
      timeoutId = setTimeout(enableTickerCatalog, 900);
    }

    return () => {
      isCancelled = true;

      if (idleId != null && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }

      if (timeoutId != null) {
        clearTimeout(timeoutId);
      }
    };
  }, [shouldDeferTickerCatalog]);

  const { data: tickerCatalog } = trpc.products.all.useQuery(undefined, {
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: isTickerCatalogReady,
  });
  const { data: trending, isLoading: trendingLoading } =
    trpc.products.trending.useQuery(
      { limit: 8 },
      { staleTime: 60_000, refetchOnWindowFocus: false }
    );
  const { trackProductClick } = useProductAnalytics();
  const trendingProducts = useMemo(
    () =>
      (trending || []).filter((product): product is Product => product != null),
    [trending]
  );
  const allProducts = useMemo(
    () =>
      (tickerCatalog || []).filter((product): product is Product => product != null),
    [tickerCatalog]
  );
  const productsBySlug = useMemo(
    () => new Map(allProducts.map(product => [product.slug, product])),
    [allProducts]
  );
  const marketShortcuts = MARKET_SHORTCUTS.map(shortcut => ({
    ...shortcut,
    title:
      "title" in shortcut
        ? shortcut.title
        : (getCatalogFilter(shortcut.slug)?.label ?? shortcut.slug),
    href: getCatalogFilterPath(shortcut.slug),
    imageUrl: toDisplayAssetUrl(productsBySlug.get(shortcut.productSlug)?.imageUrl),
  }));

  const movers = useMemo(
    () => getMovers(allProducts, trendingProducts),
    [allProducts, trendingProducts]
  );
  const activeMovers = movers[moversTab];
  const moversLoading =
    moversTab === "watched" ? trendingLoading : allProducts.length === 0;
  const moverHistories = trpc.useQueries(t =>
    activeMovers.map(product =>
      t.market.priceHistory(
        { productId: product.id, range: "1M" },
        { staleTime: 5 * 60_000, refetchOnWindowFocus: false }
      )
    )
  );

  const inFocusCandidates = trendingProducts
    .filter(product => product.currentPrice >= MOVERS_MIN_PRICE)
    .slice(0, IN_FOCUS_CANDIDATE_COUNT);
  const inFocusCandidateHistories = trpc.useQueries(t =>
    inFocusCandidates.map(product =>
      t.market.priceHistory(
        { productId: product.id, range: "3M" },
        { staleTime: 5 * 60_000, refetchOnWindowFocus: false }
      )
    )
  );
  let inFocus: { product: Product; history: PricePoint[] } | null = null;
  for (let index = 0; index < inFocusCandidates.length; index += 1) {
    const product = inFocusCandidates[index];
    const history = inFocusCandidateHistories[index];
    // Wait for earlier candidates so the pick doesn't jump once their sales load.
    if (history?.isLoading) break;
    if (history?.data && getSalesCount(history.data) >= IN_FOCUS_MIN_SALES) {
      inFocus = { product, history: history.data };
      break;
    }
  }
  const { data: inFocusRangeHistory } = trpc.market.priceHistory.useQuery(
    { productId: inFocus?.product.id ?? 0, range: chartRange },
    {
      enabled: inFocus != null && chartRange !== "3M",
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    }
  );
  const inFocusChartPoints =
    chartRange === "3M" ? inFocus?.history : inFocusRangeHistory;
  const inFocusChart = useMemo(
    () => (inFocusChartPoints ? buildPriceChart(inFocusChartPoints) : null),
    [inFocusChartPoints]
  );
  const inFocusChange = inFocus ? getMarketChangeValue(inFocus.product) : null;
  const inFocusVsRetail =
    inFocus?.product.retailPrice
      ? ((inFocus.product.currentPrice - inFocus.product.retailPrice) /
          inFocus.product.retailPrice) *
        100
      : null;
  const inFocusReleaseDate = formatReleaseDate(inFocus?.product.releaseDate);
  const sellExampleProduct =
    inFocus?.product ?? trendingProducts.find(product => product.currentPrice > 0);
  const sellExamplePayout = sellExampleProduct
    ? getSellerPayoutBreakdown(sellExampleProduct.currentPrice)
    : null;

  const tickerProducts = useMemo(() => {
    const interleavedProducts = interleaveTickerProducts(allProducts);

    return interleavedProducts.length > 0
      ? [...interleavedProducts, ...interleavedProducts]
      : [];
  }, [allProducts]);
  const tickerDuration = useMemo(() => {
    const uniqueProductCount = tickerProducts.length / 2;
    return `${Math.max(150, uniqueProductCount * 5)}s`;
  }, [tickerProducts.length]);
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    if (!heroApi) return;

    const syncHeroIndex = () => {
      setHeroIndex(heroApi.selectedScrollSnap());
    };

    syncHeroIndex();
    heroApi.on("select", syncHeroIndex);
    heroApi.on("reInit", syncHeroIndex);

    return () => {
      heroApi.off("select", syncHeroIndex);
      heroApi.off("reInit", syncHeroIndex);
    };
  }, [heroApi]);

  const handleHeroSlidePointerDown = (
    event: React.PointerEvent<HTMLAnchorElement>
  ) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;

    heroPressRef.current = {
      active: true,
      didDrag: false,
      startX: event.clientX,
    };
  };

  const handleHeroSlidePointerMove = (
    event: React.PointerEvent<HTMLAnchorElement>
  ) => {
    const heroPress = heroPressRef.current;
    if (!heroPress.active) return;

    if (Math.abs(event.clientX - heroPress.startX) > HERO_DRAG_THRESHOLD_PX) {
      heroPress.didDrag = true;
    }
  };

  const handleHeroSlidePointerEnd = () => {
    heroPressRef.current.active = false;
  };

  const handleHeroSlideClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!heroPressRef.current.didDrag) return;

    event.preventDefault();
    heroPressRef.current.didDrag = false;
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-background pb-14 sm:pb-10"
      style={{ backgroundColor: "var(--background)" }}
    >
      <SeoTags metadata={seoMetadata} />
      <Navbar />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative -mt-[6.25rem] overflow-hidden pt-[6.25rem] lg:-mt-[6.75rem] lg:pt-[6.75rem]">
          <div className="container relative pt-0 pb-6 md:pt-4 md:pb-8">
            <div
              className="relative -mx-4 cursor-grab overflow-hidden rounded-none select-none md:mx-0 md:rounded-[2rem] active:cursor-grabbing"
              ref={heroViewportRef}
            >
              <div className="flex">
                {HERO_SLIDES.map((slide, slideIndex) => (
                  <div
                    key={slide.imageUrl}
                    className="min-w-0 shrink-0 grow-0 basis-full"
                  >
                    <Link
                      href={slide.href}
                      className="block"
                      aria-label={slide.ariaLabel}
                      draggable={false}
                      onPointerDown={handleHeroSlidePointerDown}
                      onPointerMove={handleHeroSlidePointerMove}
                      onPointerUp={handleHeroSlidePointerEnd}
                      onPointerCancel={handleHeroSlidePointerEnd}
                      onClick={handleHeroSlideClick}
                      onDragStart={(event) => event.preventDefault()}
                    >
                      <div className="relative aspect-[16/7] overflow-hidden rounded-none md:aspect-[5/1] md:rounded-[1.4rem]">
                        <picture className="block h-full w-full">
                          <source
                            media={`(min-width: ${MOBILE_BREAKPOINT_PX}px)`}
                            srcSet={slide.imageUrl}
                          />
                          <img
                            src={slide.mobileImageUrl}
                            alt=""
                            className="block h-full w-full object-contain"
                            draggable={false}
                            loading={slideIndex === 0 ? "eager" : "lazy"}
                            fetchPriority={slideIndex === 0 ? "high" : "auto"}
                            decoding={slideIndex === 0 ? "sync" : "async"}
                            sizes="100vw"
                          />
                        </picture>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>

              <div className="pointer-events-none absolute inset-x-0 bottom-3 z-10 px-4 md:bottom-4 md:px-5">
                <div className="pointer-events-auto mx-auto flex w-fit items-center gap-2">
                  {HERO_SLIDES.map((slide, dotIndex) => (
                    <button
                      key={slide.imageUrl}
                      type="button"
                      aria-label={`Go to slide ${dotIndex + 1}`}
                      onClick={() => heroApi?.scrollTo(dotIndex)}
                      className={`h-2.5 w-2.5 rounded-full border transition-all ${
                        heroIndex === dotIndex
                          ? "border-white bg-white"
                          : "border-white/60 bg-white/20 hover:bg-white/35"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Market Shortcuts */}
        <nav aria-label="Markets" className="container">
          <div className="scrollbar-none -mx-4 overflow-x-auto px-4 [touch-action:pan-x] md:mx-0 md:px-0">
            <div className="mx-auto flex w-max gap-1 sm:gap-2">
              {marketShortcuts.map(shortcut => (
                <Link
                  key={shortcut.slug}
                  href={shortcut.href}
                  className="group flex w-[5.75rem] flex-col items-center gap-2.5 px-1 py-2 sm:w-[7.25rem]"
                >
                  <span className="flex h-14 w-full items-end justify-center sm:h-[4.5rem]">
                    {shortcut.imageUrl ? (
                      <img
                        src={shortcut.imageUrl}
                        alt=""
                        loading="lazy"
                        className={`max-h-14 max-w-[4.5rem] object-contain sm:max-h-[4.5rem] sm:max-w-[5.75rem] ${PHOTO_PLATE_CLASS} dark:p-1.5`}
                      />
                    ) : null}
                  </span>
                  <span className="text-[0.8rem] font-medium text-foreground transition-colors group-hover:text-primary">
                    {shortcut.title}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </nav>

        {/* Today's Movers */}
        <section aria-labelledby="home-movers" className="container pt-16 md:pt-[5.5rem]">
          <TwoToneHeading
            id="home-movers"
            title="Today's movers."
            subtitle={
              allProducts.length > 0
                ? `The biggest price changes across ${allProducts.length} devices.`
                : "The biggest price changes across every market."
            }
          />
          <div
            role="tablist"
            aria-label="Market movers"
            className="mt-8 flex gap-7 border-b border-[#E8E8ED] dark:border-white/10"
          >
            {MOVERS_TABS.map(tab => {
              const isSelected = tab.value === moversTab;

              return (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  id={`movers-tab-${tab.value}`}
                  aria-selected={isSelected}
                  aria-controls="movers-panel"
                  onClick={() => setMoversTab(tab.value)}
                  className={`-mb-px h-11 border-b-2 text-[0.95rem] font-medium transition-colors ${
                    isSelected
                      ? "border-foreground text-foreground"
                      : "border-transparent text-[#6E6E73] hover:text-foreground dark:text-zinc-400"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div
            id="movers-panel"
            role="tabpanel"
            aria-labelledby={`movers-tab-${moversTab}`}
            className="scrollbar-none -mx-4 mt-6 grid snap-x snap-mandatory auto-cols-[minmax(13rem,1fr)] grid-flow-col gap-4 overflow-x-auto px-4 pb-4 pt-1 [touch-action:pan-x] md:mx-0 md:px-0.5"
          >
            {moversLoading
              ? Array.from({ length: MOVERS_LIMIT }).map((_, index) => (
                  <div
                    key={index}
                    className="h-[19.5rem] animate-pulse rounded-[1.125rem] bg-[#F5F5F7] dark:bg-white/[0.04]"
                  />
                ))
              : activeMovers.map((product, index) => (
                  <MarketProductCard
                    key={product.id}
                    product={product}
                    subtitle={product.brand}
                    history={moverHistories[index]?.data}
                    isHistoryLoading={moverHistories[index]?.isLoading ?? false}
                    onClick={() =>
                      trackProductClick(product.id, `home-movers-${moversTab}`)
                    }
                    className="snap-start"
                  />
                ))}
          </div>
          <p className="mt-2 text-xs leading-5 text-[#6E6E73] dark:text-zinc-400">
            Devices priced over {formatPrice(MOVERS_MIN_PRICE)}. Busy models
            compare the last 7 days with the 7 before; quieter models compare 30
            days. Lines show the 30-day trend of daily sold prices.
          </p>
        </section>

        {/* In Focus */}
        <section
          aria-labelledby="home-in-focus"
          className="mt-20 bg-[#F5F5F7] dark:bg-white/[0.03] md:mt-24"
        >
          <div className="container py-16 md:py-[5.5rem]">
            {inFocus ? (
              <>
                <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
                  <div className="flex h-64 items-center justify-center sm:h-[25rem]">
                    {inFocus.product.imageUrl ? (
                      <img
                        src={toDisplayAssetUrl(inFocus.product.imageUrl)}
                        alt={inFocus.product.name}
                        className="max-h-full max-w-[80%] object-contain mix-blend-multiply dark:rounded-3xl dark:bg-white dark:p-6 dark:mix-blend-normal"
                      />
                    ) : null}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#B4581A] dark:text-orange-300">
                      In focus
                    </p>
                    <h2
                      id="home-in-focus"
                      className="mt-2 text-[2.25rem] font-semibold leading-[1.05] tracking-[-0.035em] text-foreground sm:text-5xl"
                    >
                      {inFocus.product.marketModel}
                    </h2>
                    <p className="mt-2.5 text-[1.05rem] text-[#6E6E73] dark:text-zinc-400">
                      {inFocus.product.brand}
                      {inFocusReleaseDate ? ` · Released ${inFocusReleaseDate}` : ""}
                    </p>
                    <div className="mt-7 flex flex-wrap items-baseline gap-x-3.5 gap-y-1.5">
                      <span className="font-mono text-[2.25rem] font-medium tracking-[-0.03em] text-foreground sm:text-[2.75rem]">
                        {formatExactPrice(inFocus.product.currentPrice)}
                      </span>
                      {inFocusChange != null ? (
                        <span
                          className={`font-mono text-[1.05rem] font-medium ${getChangeColorClass(inFocusChange)}`}
                        >
                          {formatChange(inFocusChange)}
                        </span>
                      ) : null}
                    </div>
                    <dl className="mt-7 grid grid-cols-3 gap-5 border-t border-[#D2D2D7] pt-5 dark:border-white/10">
                      <div>
                        <dt className="text-[0.8rem] text-[#6E6E73] dark:text-zinc-400">Retail</dt>
                        <dd className="mt-1 font-mono text-[1.05rem] font-medium text-foreground">
                          {formatPrice(inFocus.product.retailPrice)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[0.8rem] text-[#6E6E73] dark:text-zinc-400">Vs. retail</dt>
                        <dd className="mt-1 font-mono text-[1.05rem] font-medium text-foreground">
                          {inFocusVsRetail == null
                            ? "—"
                            : `${inFocusVsRetail >= 0 ? "+" : "−"}${Math.abs(inFocusVsRetail).toFixed(1)}%`}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[0.8rem] text-[#6E6E73] dark:text-zinc-400">Sales, 90 days</dt>
                        <dd className="mt-1 font-mono text-[1.05rem] font-medium text-foreground">
                          {salesCountFormatter.format(getSalesCount(inFocus.history))}
                        </dd>
                      </div>
                    </dl>
                    <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
                      <Link
                        href={getProductPath(inFocus.product)}
                        onClick={() =>
                          inFocus && trackProductClick(inFocus.product.id, "home-in-focus")
                        }
                        className="inline-flex h-11 items-center rounded-full bg-primary px-[1.375rem] text-[0.95rem] font-medium text-primary-foreground transition-colors hover:bg-[#1E4FB5]"
                      >
                        View market
                      </Link>
                      <Link
                        href="/sell"
                        className="text-[0.95rem] text-primary hover:underline"
                      >
                        Sell yours ›
                      </Link>
                    </div>
                  </div>
                </div>

                <figure className="mt-12 rounded-3xl bg-white px-5 pb-6 pt-6 dark:bg-card sm:px-8 sm:pt-7">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <figcaption className="text-[0.95rem] font-semibold text-foreground">
                      {CHART_RANGES.find(range => range.value === chartRange)?.title}
                    </figcaption>
                    <div
                      role="group"
                      aria-label="Chart range"
                      className="flex gap-0.5 rounded-full bg-[#F5F5F7] p-[3px] dark:bg-white/[0.06]"
                    >
                      {CHART_RANGES.map(range => {
                        const isSelected = range.value === chartRange;

                        return (
                          <button
                            key={range.value}
                            type="button"
                            aria-pressed={isSelected}
                            onClick={() => setChartRange(range.value)}
                            className={`h-[1.875rem] rounded-full px-3.5 text-[0.8rem] font-medium transition-colors ${
                              isSelected
                                ? "bg-white font-semibold text-foreground shadow-[0_1px_3px_rgba(17,17,19,0.12)] dark:bg-white/[0.14]"
                                : "text-[#6E6E73] hover:text-foreground dark:text-zinc-400"
                            }`}
                          >
                            {range.value}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {inFocusChart ? (
                    <>
                      <div className="relative mt-6 h-60">
                        <svg
                          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
                          preserveAspectRatio="none"
                          width="100%"
                          height="240"
                          role="img"
                          aria-label={`${inFocus.product.name} sold prices. The ${inFocusChart.windowDays}-day median moved from ${formatPrice(inFocusChart.firstTrend)} to ${formatPrice(inFocusChart.lastTrend)}.`}
                          className="block overflow-visible text-primary"
                        >
                          {inFocusChart.ticks.map(tick => (
                            <line
                              key={tick.label}
                              x1="0"
                              x2={CHART_WIDTH}
                              y1={(tick.top / 100) * CHART_HEIGHT}
                              y2={(tick.top / 100) * CHART_HEIGHT}
                              stroke="currentColor"
                              className="text-[#F0F0F3] dark:text-white/[0.06]"
                              strokeWidth={1}
                              vectorEffect="non-scaling-stroke"
                            />
                          ))}
                          <path
                            d={inFocusChart.areaPath}
                            fill="currentColor"
                            fillOpacity={0.06}
                          />
                          <path
                            d={inFocusChart.trendPath}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2.25}
                            strokeLinejoin="round"
                            strokeLinecap="round"
                            vectorEffect="non-scaling-stroke"
                          />
                        </svg>
                        {inFocusChart.ticks.map(tick => (
                          <span
                            key={tick.label}
                            className="absolute left-0 -translate-y-1/2 bg-white pr-2 font-mono text-[0.7rem] text-[#86868B] dark:bg-card"
                            style={{ top: `${tick.top}%` }}
                          >
                            {tick.label}
                          </span>
                        ))}
                      </div>
                      <div className="relative mt-2.5 h-4 font-mono text-[0.7rem] text-[#6E6E73] dark:text-zinc-400">
                        {inFocusChart.dateLabels.map(dateLabel => (
                          <span
                            key={dateLabel.left}
                            className={`absolute whitespace-nowrap ${
                              dateLabel.align === "start"
                                ? ""
                                : dateLabel.align === "end"
                                  ? "-translate-x-full"
                                  : "-translate-x-1/2"
                            }`}
                            style={{ left: `${dateLabel.left}%` }}
                          >
                            {dateLabel.label}
                          </span>
                        ))}
                      </div>
                      <p className="mt-3.5 text-xs text-[#6E6E73] dark:text-zinc-400">
                        {inFocusChart.windowDays}-day median of daily sold prices on
                        eBay and techfare.
                      </p>
                    </>
                  ) : (
                    <div className="mt-6 h-[17.5rem] animate-pulse rounded-2xl bg-[#F5F5F7] dark:bg-white/[0.04]" />
                  )}
                </figure>
              </>
            ) : (
              <div className="h-[34rem] animate-pulse rounded-3xl bg-white/60 dark:bg-white/[0.03]" />
            )}
          </div>
        </section>

        {/* Sell */}
        <section aria-labelledby="home-sell" className="container pt-20 md:pt-24">
          <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:gap-14">
            <div>
              <TwoToneHeading
                id="home-sell"
                title="Sell on techfare."
                subtitle="Priced by the market, paid through Stripe or PayPal."
              />
              <p className="mt-5 max-w-[30rem] text-[1.05rem] leading-7 text-[#424245] dark:text-zinc-300">
                Every listing starts from the live price for your exact model,
                so buyers see a fair price and you get it. One flat{" "}
                {Math.round(PLATFORM_FEE_RATE * 100)}% platform fee.
              </p>
              <Link
                href="/sell"
                className="mt-7 inline-flex h-11 items-center rounded-full bg-foreground px-[1.375rem] text-[0.95rem] font-medium text-background transition-opacity hover:opacity-90"
              >
                Start selling
              </Link>
            </div>

            {sellExampleProduct && sellExamplePayout ? (
              <div className="rounded-[1.25rem] bg-[#F5F5F7] p-7 text-foreground dark:bg-card dark:ring-1 dark:ring-inset dark:ring-white/10">
                <div className="flex items-center gap-3.5">
                  <span className="flex h-[3.75rem] w-[3.25rem] shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white">
                    {sellExampleProduct.imageUrl ? (
                      <img
                        src={toDisplayAssetUrl(sellExampleProduct.imageUrl)}
                        alt=""
                        loading="lazy"
                        className="max-h-[80%] max-w-[76%] object-contain"
                      />
                    ) : null}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[0.95rem] font-semibold">
                      {sellExampleProduct.marketModel}
                    </span>
                    <span className="mt-0.5 block text-[0.8rem] text-[#6E6E73] dark:text-zinc-400">
                      Sold at today's market price
                    </span>
                  </span>
                </div>
                <dl className="mt-5 text-sm">
                  <div className="flex justify-between gap-3 border-t border-[#E3E3E8] py-3 dark:border-white/10">
                    <dt className="text-[#424245] dark:text-zinc-300">Sale price</dt>
                    <dd className="shrink-0 whitespace-nowrap font-mono">
                      {formatExactPrice(sellExampleProduct.currentPrice)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3 border-t border-[#E3E3E8] py-3 dark:border-white/10">
                    <dt className="text-[#424245] dark:text-zinc-300">
                      Platform fee ({Math.round(PLATFORM_FEE_RATE * 100)}%)
                    </dt>
                    <dd className="shrink-0 whitespace-nowrap font-mono">
                      −{formatExactPrice(sellExamplePayout.platformFee)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3 border-t border-[#E3E3E8] py-3 dark:border-white/10">
                    <dt className="text-[#424245] dark:text-zinc-300">
                      Payment fee (up to {(PAYPAL_FEE_RATE * 100).toFixed(2)}% +{" "}
                      {formatExactPrice(PAYPAL_FIXED_FEE)})
                    </dt>
                    <dd className="shrink-0 whitespace-nowrap font-mono">
                      −{formatExactPrice(sellExamplePayout.processingFee)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3 border-t border-[#E3E3E8] py-3 dark:border-white/10">
                    <dt className="text-[#424245] dark:text-zinc-300">Shipping fee</dt>
                    <dd className="shrink-0 whitespace-nowrap font-mono">
                      {formatExactPrice(sellExamplePayout.shippingFee)}
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 border-t border-foreground pt-4">
                    <dt className="font-semibold">You receive</dt>
                    <dd className="shrink-0 whitespace-nowrap font-mono text-[1.6rem] font-medium">
                      {formatExactPrice(sellExamplePayout.payout)}
                    </dd>
                  </div>
                </dl>
              </div>
            ) : (
              <div className="h-[22rem] animate-pulse rounded-[1.25rem] bg-[#F5F5F7] dark:bg-white/[0.04]" />
            )}
          </div>
        </section>

        {/* How We Price */}
        <section aria-labelledby="home-pricing" className="container pt-24 md:pt-28">
          <TwoToneHeading
            id="home-pricing"
            title="How we price."
            subtitle="Real sales, never asking prices."
          />
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {PRICING_POINTS.map(point => (
              <div
                key={point.title}
                className="rounded-[1.25rem] bg-[#F5F5F7] p-7 dark:bg-card dark:ring-1 dark:ring-inset dark:ring-white/[0.06]"
              >
                <h3 className="text-[1.2rem] font-semibold tracking-[-0.015em] text-foreground">
                  {point.title}
                </h3>
                <p className="mt-2.5 text-[0.95rem] leading-6 text-[#424245] dark:text-zinc-300">
                  {point.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Value Guides */}
        <section aria-labelledby="home-guides" className="container pt-24 pb-16 md:pt-28 md:pb-24">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h2
              id="home-guides"
              className="text-[1.75rem] font-semibold tracking-[-0.02em] text-foreground"
            >
              Value guides
            </h2>
            <Link href="/guides" className="text-[0.95rem] text-primary hover:underline">
              All value guides ›
            </Link>
          </div>
          <div className="mt-5 grid border-t border-[#E8E8ED] dark:border-white/10 md:grid-cols-3 md:gap-x-10">
            {POPULAR_VALUE_GUIDES.map(guide => (
              <Link
                key={guide.slug}
                href={getCatalogFilterPath(guide.slug)}
                className="group flex justify-between gap-4 border-b border-[#E8E8ED] py-[1.125rem] dark:border-white/10"
              >
                <span>
                  <span className="block text-[0.95rem] font-medium text-foreground transition-colors group-hover:text-primary">
                    {guide.title}
                  </span>
                  <span className="mt-1 block text-[0.8rem] leading-5 text-[#6E6E73] dark:text-zinc-400">
                    {guide.description}
                  </span>
                </span>
                <span aria-hidden="true" className="text-lg leading-tight text-[#86868B]">
                  ›
                </span>
              </Link>
            ))}
          </div>
          <p className="mt-5 text-[0.8rem] leading-7 text-[#6E6E73] dark:text-zinc-400">
            Model families:{" "}
            {POPULAR_MODEL_FAMILY_GUIDES.map((guide, index) => (
              <span key={guide.slug}>
                {index > 0 ? " · " : ""}
                <Link
                  href={getModelFamilyGuidePath(guide.slug)}
                  className="text-primary hover:underline"
                >
                  {guide.title}
                </Link>
              </span>
            ))}
          </p>
        </section>
      </main>

      <Footer />

      <section className="home-market-ticker fixed inset-x-0 bottom-0 z-40 block border-t border-border/80 bg-white dark:bg-background">
        <div aria-hidden="true" className="ticker-sonic-layer">
          <img
            src={SONIC_RUNNING_GIF_URL}
            alt=""
            width={30}
            height={30}
            className="ticker-sonic-sprite"
            loading="eager"
            decoding="async"
          />
        </div>
        <div className="container py-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] md:py-2">
          <div className="flex items-center overflow-hidden">
            <div className="hero-ticker-mask relative min-w-0 flex-1 overflow-hidden">
              {tickerProducts.length > 0 ? (
                <div
                  className="hero-ticker-track flex min-w-max items-center gap-2 md:gap-2.5"
                  style={
                    {
                      "--hero-ticker-duration": tickerDuration,
                    } as CSSProperties
                  }
                >
                  {tickerProducts.map((product, index) => {
                    const changePercent = parseResolvedMarketChange(
                      product.priceChangePercent24h
                    );
                    const isPositive = (changePercent.value ?? 0) >= 0;

                    return (
                      <Link
                        key={`${product.id}-${index}`}
                        href={getProductPath(product)}
                        onClick={() =>
                          trackProductClick(product.id, "home-ticker")
                        }
                        className="shrink-0"
                      >
                        <div className="inline-flex min-w-[9.25rem] shrink-0 items-center justify-between gap-1.5 whitespace-nowrap rounded-md border border-border/80 bg-card/80 px-2 py-1 text-[11px] transition-colors hover:border-primary/30 hover:bg-card sm:min-w-[11rem] sm:gap-2.5 sm:px-2.5 sm:text-xs">
                          <span className="shrink-0 font-medium text-foreground">
                            {product.name}
                          </span>
                          <div className="inline-flex shrink-0 items-center gap-1.5 sm:gap-2">
                            <span
                              className={`inline-flex shrink-0 items-center gap-0.5 font-semibold ${
                                !changePercent.hasResolvedChange
                                  ? "text-muted-foreground"
                                  : isPositive
                                  ? "text-emerald-600"
                                  : "text-destructive"
                              }`}
                            >
                              {changePercent.hasResolvedChange ? (
                                isPositive ? (
                                  <TrendingUp className="h-3 w-3" />
                                ) : (
                                  <TrendingDown className="h-3 w-3" />
                                )
                              ) : null}
                              {changePercent.hasResolvedChange
                                ? `${isPositive ? "+" : ""}${(changePercent.value ?? 0).toFixed(1)}%`
                                : "--"}
                            </span>
                            <span className="shrink-0 font-mono text-[12px] font-bold text-foreground sm:text-[13px]">
                              {formatPrice(product.currentPrice)}
                            </span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center gap-2.5">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div
                      key={index}
                      className="h-8 w-40 rounded-md bg-secondary animate-pulse"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
